import { useContext, useEffect, useState } from 'react';
import { NextPage } from 'next';

import { Routes } from 'types/routes';
import { Section } from 'types/sections';

import CmsSection from 'models/CmsSection';
import { Visibilities } from 'models/Product';

import { withVariables } from 'utils/string';

import LocaleContext from 'components/context/Locale';
import Breadcrumbs from 'components/layout/Breadcrumbs/component';
import Head from 'components/layout/Head';
import { OGTypes } from 'components/layout/Head/types';
import PageContainer from 'components/layout/PageContainer';
import SectionsWrapper from 'components/modules/public/cms/SectionsWrapper';
import SectionWrapper from 'components/modules/public/cms/SectionWrapper';
import Portal from 'components/modules/public/layout/modals/Portal';
import PageNotFound from 'components/modules/public/pages/NotFound';
import WrapperPublic from 'components/wrappers/Public';

import SectionAccessBar from './sections/AccessBar';
import SectionArticle from './sections/Article';
import SectionBlogs from './sections/Blogs';
import SectionContact from './sections/Contact';
import SectionHero from './sections/Hero';
import SectionProducts from './sections/Products';
import SectionPromoBar from './sections/PromoBar';
import { Positions } from './sections/PromoBar/types';
import StyledComponent from './styles';
import { Props } from './types';

const PublicPageArticle: NextPage<Props> = ({
    article,
    articles,
    relatedArticles,
    articleCategories,
    products,
    notFound,
    cmsPage,
}) => {
    if (notFound) return <PageNotFound />;
    const [promobarInitalTopOffset, setPromobarInitalTopOffset] = useState<number>(null);
    const [removeScrollBlocker, setRemoveScrollBlocker] = useState<boolean>(false);
    const [attempts, setAttempts] = useState<number>(0);
    const localeContext = useContext(LocaleContext);
    const translations = localeContext?.translations?.article;
    const translationsBreadcrumbs = localeContext?.translations?.breadcrumbs;

    useEffect(() => {
        const divElement = document.getElementById('#promo-bar-relative');
        //TODO: check why divElement is null|undefined when you are back from cart page
        if (!divElement && attempts < 3) setAttempts(attempts + 1);
        const topOffset = divElement?.getBoundingClientRect().top + window.scrollY - window.innerHeight;
        setPromobarInitalTopOffset(topOffset);
    }, [attempts]);

    const breadcrumbs = [{
        to: Routes.PublicHome,
        label: translationsBreadcrumbs.home,
    }, {
        to: Routes.PublicBlog,
        label: translationsBreadcrumbs.blogs,
    }, {
        to: withVariables(Routes.PublicBlogCategory, { articleCategorySlug: article?.data?.category?.locale?.slug }),
        label: article?.data?.category?.locale?.name,
    }, {
        to: article?.data?.href,
        label: article?.data?.locale?.title,
        active: true,
    }];

    const sections: Section[] = [
        {
            slug: 'hero',
            order: 1,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionHero
                            translations={{
                                ...translations?.content?.hero,
                                ...locale,
                            }}
                            override={() => override({
                                translations: {
                                    headline: `<h1>${article?.data?.locale?.title}</h1>`,
                                    subheadline: article?.data?.publishedAtDisplayAlterFormat,
                                    background: article?.data?.locale?.image?.imageUrl,
                                    backgroundMobile: article?.data?.locale?.imageMobile?.imageUrl,
                                    quality: 90,
                                },
                            })}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'breadcrumbs',
            order: 2,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale) => (<Breadcrumbs breadcrumbs={breadcrumbs} />)}
                </SectionWrapper>
            ),
        },
        {
            slug: 'promoBar',
            order: 3,
            visible: article?.data?.locale?.articlePromoContent?.promoBarVisible,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale) => {
                        return (
                            <SectionPromoBar
                                articlePromoContent={article?.data?.locale?.articlePromoContent}
                                position={Positions.Relative}
                                sectionId='#promo-bar-relative'
                            />
                        );
                    }}
                </SectionWrapper>
            ),
        },
        {
            slug: 'article',
            order: 4,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale) => (
                        <SectionArticle
                            {...translations?.content?.articleCopy}
                            articleContentCopy={{
                                listHeadline: locale?.listHeadline,
                                rate: {
                                    rateTitle: locale?.rateTitle,
                                    ratingTitle: locale?.ratingTitle,
                                    ratedTitle: locale?.ratedTitle,
                                    ratingsCountTitle: locale?.ratingsCountTitle,
                                    noRating: locale?.noRating,
                                },
                                author: {
                                    specializtionTitle: locale?.specializtionTitle,
                                    bioTitle: locale?.bioTitle,
                                },
                                socials: {
                                    socialsTitle: locale?.socialsTitle,
                                },
                            }}
                            {...locale}
                            article={article?.data}
                            articleCategories={articleCategories?.elements}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'blogs',
            order: 5,
            visible: articles?.elements?.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionBlogs
                            translations={{
                                ...translations?.content?.blogs,
                                ...locale,
                            }}
                            override={() => override({
                                articles: articles.elements,
                            })}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'relatedArticles',
            order: 6,
            visible: relatedArticles?.elements?.length > 0 || article?.data?.relatedArticles?.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionBlogs
                            translations={{
                                ...translations?.content?.blogs,
                                ...locale,
                            }}
                            override={() => override({
                                articles: relatedArticles?.elements || article?.data?.relatedArticles,
                            })}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'offers',
            order: 7,
            visible: products?.elements?.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => {
                        return (
                            <SectionProducts
                                translations={{
                                    ...translations?.content?.products,
                                    ...locale,
                                }}
                                override={() => override({
                                    products,
                                    visibility: Visibilities.Article,
                                    articleSection: true,
                                })}
                            />
                        );
                    }}
                </SectionWrapper>
            ),
        },
        {
            slug: 'contact',
            order: 8,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionContact
                            locale={locale}
                            override={override}
                        />
                    )}
                </SectionWrapper>
            ),
        },
    ];

    return (
        <WrapperPublic
            isBannerVisible={cmsPage?.data?.isBannerVisible}
            bannerContent={cmsPage?.data?.bannerContent}
            bannerLink={cmsPage?.data?.bannerLink}
        >
            <StyledComponent className="public-page-article">
                <Head
                    title={article?.data?.locale?.metaTitle || cmsPage?.data?.locale?.title || translations?.meta?.title}
                    description={article?.data?.locale?.metaDescription || cmsPage?.data?.locale?.description || translations?.meta?.description}
                    keywords={cmsPage?.data?.locale?.keywords || translations?.meta?.keywords}
                    tags={cmsPage?.data?.head}
                    ogType={OGTypes.Article}
                    ogImage={article?.data?.locale?.image?.imageUrl}
                    cmsPageIndexingEnabled={cmsPage?.data?.indexingEnabled}
                />
                <PageContainer>
                    <SectionsWrapper
                        sections={sections}
                        cmsSections={cmsPage?.data?.sections}
                    />
                    {article?.data?.isProfessional && (
                        <Portal removeScrollBlocker={removeScrollBlocker}>
                            <SectionAccessBar
                                articleHref={article?.data?.href}
                                onSuccess={() => setRemoveScrollBlocker(true)}
                            />
                        </Portal>
                    )}
                    {article?.data?.locale?.articlePromoContent && (
                        <SectionPromoBar
                            articlePromoContent={article?.data?.locale?.articlePromoContent}
                            position={Positions.Fixed}
                            promobarInitalTopOffset={promobarInitalTopOffset}
                        />
                    )}
                </PageContainer>
            </StyledComponent>
        </WrapperPublic>

    );
};

export default PublicPageArticle;
